import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Moment from "react-moment";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactToPrint from "react-to-print";
import { VzPageContainer } from "../../../layouts/VzPageContainer";
import FilePreviewModal from "../../../layouts/FilePreview/FilePreviewModal";
import axios from "../../../apis/Api";
import "./SCRV2SummaryReport.css";
import "./SCRV2CompleteDetailReport.css";
import {
  getSCRV2CompleteDetail,
  isSCRV2DetailComplete,
  normalizeSCRV2DetailReport,
  peekSCRV2DetailReportCache,
  prefetchSCRV2SummaryReport,
  buildSCRV2SummaryPreview,
  fetchSCRV2InvoiceDetail,
  peekSCRV2InvoiceDetailCache,
  prefetchSCRV2InvoiceDetail,
  exportSCRV2InvoiceWiseDetail,
} from "./SCRV2DetailReportActions";
import {
  buildInvoiceActivitySections,
  buildNormalInvoiceRows,
  filterInvoiceRows,
  formatCurrency,
  getReportHighlights,
} from "./SCRV2InvoiceDetailHelpers";
import SCRV2CompleteDetailPrint from "./SCRV2CompleteDetailPrint";
import SCRV2InvoiceDetailModal from "./SCRV2InvoiceDetailModal";
import {
  renderScrV2SummaryColumn,
  SCR_V2_NESTED_CARD_STYLE,
  ScrV2SummaryKpi,
} from "./SCRV2SummaryUiShared";
import {
  formatScrV2DurationDays,
  renderScrV2IdentityDate,
} from "./SCRV2IdentityPresentation";
import { matchesScrV2Id, resolveInitialScrV2Report } from "./SCRV2ReportStateHelpers";
import {
  InvoiceActivityBadges,
  splitActivityBadgeLabels,
} from "./SCRV2InvoiceActivityBadges";

const NESTED_CARD_STYLE = SCR_V2_NESTED_CARD_STYLE;

const resolveInitialDetailReport = (scrId, location, reduxDetail) =>
  resolveInitialScrV2Report({
    scrId,
    location,
    reduxReport: reduxDetail,
    previewStateKey: "detailPreview",
    peekCache: peekSCRV2DetailReportCache,
    isComplete: isSCRV2DetailComplete,
    normalize: normalizeSCRV2DetailReport,
  });

const renderSummaryColumn = renderScrV2SummaryColumn;
const SummaryKpi = ScrV2SummaryKpi;

const InvoiceNoCell = ({ row }) => {
  const badgeLabels = useMemo(
    () => splitActivityBadgeLabels(row.sectionActivityBadge),
    [row.sectionActivityBadge]
  );

  return (
    <div className="scr-invoice-table__invoice-no-inner">
      <span className="scr-invoice-table__invoice-no-text">{row.invoiceNo || "-"}</span>
      {badgeLabels.length > 0 ? (
        <InvoiceActivityBadges
          labels={badgeLabels}
          sectionId={row.sectionId}
          rowKey={row.key}
        />
      ) : null}
    </div>
  );
};

const InvoiceActivityTable = ({
  rows,
  openInvoiceDetail,
  prefetchInvoiceDetail,
}) => (
  <Table responsive size="sm" className="mb-0 scr-invoice-table">
    <thead className="thead-light">
      <tr>
        <th className="pl-3 py-2">VizShop Code</th>
        <th className="py-2">Shop Name</th>
        <th className="py-2">Invoice No.</th>
        {/* TEMP HIDDEN: Invoice Amount column — restore header + cell + CSS nth-child widths together
        <th className="text-right py-2">Invoice Amount</th>
        */}
        <th className="text-right py-2">Received Amount</th>
        <th className="py-2">Invoice Date</th>
        <th className="text-center pr-3 py-2 d-print-none">Actions</th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 ? (
        <tr>
          <td colSpan="6" className="text-center text-muted py-4">
            No invoices found.
          </td>
        </tr>
      ) : (
        rows.map((row) => (
          <tr key={row.key}>
            <td className="pl-3 align-middle">{row.vizShopCode || "-"}</td>
            <td className="align-middle">{row.shopName || "-"}</td>
            <td className="align-middle font-weight-bold scr-invoice-table__invoice-no">
              <InvoiceNoCell row={row} />
            </td>
            {/* TEMP HIDDEN: Invoice Amount column
            <td className="text-right align-middle font-weight-bold scr-invoice-table__amount">
              {formatCurrency(row.invoiceAmount)}
            </td>
            */}
            <td className="text-right align-middle font-weight-bold scr-invoice-table__received">
              {formatCurrency(row.receivedAmount)}
            </td>
            <td className="align-middle scr-invoice-table__date">
              {row.invoiceDate ? (
                <Moment format="DD-MM-YYYY">{row.invoiceDate}</Moment>
              ) : (
                "-"
              )}
            </td>
            <td className="text-center pr-3 align-middle d-print-none">
              <Button
                size="sm"
                variant="outline-primary"
                className="scr-invoice-view-detail-btn"
                onClick={() => openInvoiceDetail(row)}
                onMouseEnter={() => prefetchInvoiceDetail(row)}
                onFocus={() => prefetchInvoiceDetail(row)}
              >
                <i className="fas fa-eye mr-1" />
                View Detail
              </Button>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </Table>
);

const SkeletonBlock = ({ height = 14, width = "100%", className = "" }) => (
  <div
    className={`scr-invoice-detail-skeleton ${className}`.trim()}
    style={{ height, width }}
    aria-hidden="true"
  />
);

const InvoiceActivitySectionsSkeleton = () => (
  <div className="scr-invoice-list-skeleton" aria-busy="true" aria-live="polite" aria-label="Loading invoices">
    {[0, 1].map((sectionIndex) => (
      <div
        key={sectionIndex}
        className="scr-invoice-activity-section scr-invoice-activity-section--skeleton"
      >
        <div className="scr-invoice-activity-section__header scr-invoice-list-skeleton__header">
          <span className="scr-invoice-activity-section__title">
            <SkeletonBlock height={28} width={28} className="scr-invoice-list-skeleton__icon" />
            <SkeletonBlock height={14} width={148} />
            <SkeletonBlock height={20} width={28} className="scr-invoice-list-skeleton__pill" />
            <SkeletonBlock height={14} width={72} />
          </span>
          <SkeletonBlock height={26} width={26} className="scr-invoice-list-skeleton__pill" />
        </div>
        <div className="scr-invoice-activity-section__body">
          <Table responsive size="sm" className="mb-0 scr-invoice-table">
            <thead className="thead-light">
              <tr>
                <th className="pl-3 py-2">VizShop Code</th>
                <th className="py-2">Shop Name</th>
                <th className="py-2">Invoice No.</th>
                {/* TEMP HIDDEN: Invoice Amount column (skeleton header)
                <th className="text-right py-2">Invoice Amount</th>
                */}
                <th className="text-right py-2">Received Amount</th>
                <th className="py-2">Invoice Date</th>
                <th className="text-center pr-3 py-2 d-print-none">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2].map((rowIndex) => (
                <tr key={rowIndex}>
                  <td className="pl-3 align-middle">
                    <SkeletonBlock height={12} width="78%" />
                  </td>
                  <td className="align-middle">
                    <SkeletonBlock height={12} width="88%" />
                  </td>
                  <td className="align-middle">
                    <SkeletonBlock height={12} width="70%" />
                  </td>
                  {/* TEMP HIDDEN: Invoice Amount column (skeleton cell)
                  <td className="text-right align-middle">
                    <SkeletonBlock height={12} width="64%" className="ml-auto" />
                  </td>
                  */}
                  <td className="text-right align-middle">
                    <SkeletonBlock height={12} width={64} className="ml-auto" />
                  </td>
                  <td className="align-middle">
                    <SkeletonBlock height={12} width="72%" />
                  </td>
                  <td className="text-center pr-3 align-middle d-print-none">
                    <SkeletonBlock height={24} width={88} className="scr-invoice-list-skeleton__pill mx-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    ))}
  </div>
);

const InvoiceDetailPageShellSkeleton = () => (
  <div className="scr-v2-summary-screen scr-invoice-detail-screen" aria-busy="true" aria-live="polite">
    <div className="scr-summary-page-header d-flex align-items-center flex-wrap mb-2">
      <div className="scr-summary-page-header__title">
        <h5 className="mb-0">
          <b>SCR Invoice Wise Detail</b>
        </h5>
      </div>
      <div className="scr-summary-page-header__spacer flex-grow-1" aria-hidden="true" />
      <div className="scr-summary-page-header__status text-right">
        <SkeletonBlock height={22} width={72} className="scr-invoice-list-skeleton__pill ml-auto mb-1" />
        <SkeletonBlock height={12} width={96} className="ml-auto" />
      </div>
    </div>

    <div className="card border mb-2 scr-summary-info-card" style={NESTED_CARD_STYLE}>
      <div className="card-body py-2 px-2">
        <Row className="align-items-center mx-0">
          {[0, 1, 2, 3, 4].map((index) => (
            <Col
              key={index}
              xs={6}
              md={4}
              lg={index < 2 ? 3 : 2}
              className="px-1 py-1"
            >
              <SkeletonBlock height={10} width="55%" className="mb-1" />
              <SkeletonBlock height={14} width="85%" />
            </Col>
          ))}
        </Row>
      </div>
    </div>

    <Row className="mb-2 mx-0 align-items-stretch scr-summary-kpi-row">
      {[0, 1, 2].map((index) => (
        <Col
          key={index}
          xs={12}
          lg={4}
          className={`mb-2 mb-lg-0 ${
            index === 0 ? "ps-0 pe-lg-1" : index === 2 ? "ps-lg-1 pe-0" : "px-lg-1"
          }`}
        >
          <div className="card border h-100 scr-summary-kpi-card" style={NESTED_CARD_STYLE}>
            <div className="card-body py-3 px-3 d-flex align-items-center">
              <SkeletonBlock height={40} width={40} className="scr-invoice-list-skeleton__icon mr-3" />
              <div className="flex-grow-1">
                <SkeletonBlock height={10} width="48%" className="mb-2" />
                <SkeletonBlock height={18} width="62%" className="mb-1" />
                <SkeletonBlock height={10} width="40%" />
              </div>
            </div>
          </div>
        </Col>
      ))}
    </Row>

    <div className="card border mb-2 scr-invoice-table-card" style={NESTED_CARD_STYLE}>
      <div className="card-header py-2 px-2 bg-white border-bottom">
        <Row className="align-items-center mx-0">
          <Col xs={12} lg={6} className="px-0 mb-2 mb-lg-0">
            <SkeletonBlock height={14} width={160} />
          </Col>
          <Col xs={12} lg={6} className="px-0 d-flex justify-content-lg-end">
            <SkeletonBlock height={30} width={88} className="scr-invoice-list-skeleton__pill mr-2" />
            <SkeletonBlock height={30} width={220} className="scr-invoice-list-skeleton__pill" />
          </Col>
        </Row>
      </div>
      <div className="card-body p-0">
        <InvoiceActivitySectionsSkeleton />
      </div>
    </div>
  </div>
);

const SCRV2CompleteDetailReport = (props) => {
  const history = useHistory();
  const location = useLocation();
  const scrId = props.match?.params?.scrId;
  const loadRequestRef = useRef(0);
  const [report, setReport] = useState(() =>
    resolveInitialDetailReport(scrId, location, props.detail)
  );
  const [isRefreshing, setIsRefreshing] = useState(
    () => !isSCRV2DetailComplete(resolveInitialDetailReport(scrId, location, props.detail))
  );

  const hasFullReport = isSCRV2DetailComplete(report);
  const showTablePlaceholders = isRefreshing && !hasFullReport;

  const [searchText, setSearchText] = useState("");
  const [openSections, setOpenSections] = useState({});
  const [selectedInvoiceRow, setSelectedInvoiceRow] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [invoiceDetailError, setInvoiceDetailError] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const invoiceDetailRequestRef = useRef(0);
  const printRef = useRef(null);
  const printPayloadRef = useRef(null);
  const [isPrintMountReady, setIsPrintMountReady] = useState(false);

  useEffect(() => {
    if (!scrId) {
      return;
    }

    const requestId = ++loadRequestRef.current;
    const initialReport = resolveInitialDetailReport(scrId, location, props.detail);
    const hasCachedReport = isSCRV2DetailComplete(initialReport);
    setReport(initialReport);
    setIsRefreshing(!hasCachedReport);

    const shouldBackgroundRefresh = hasCachedReport && !initialReport.isFinalized;
    if (shouldBackgroundRefresh) {
      setIsRefreshing(true);
    }

    props
      .getSCRV2CompleteDetail({
        scrId,
        silent: hasCachedReport,
      })
      .then((payload) => {
        if (requestId !== loadRequestRef.current || !payload) {
          return;
        }

        setReport(normalizeSCRV2DetailReport(payload, scrId));
      })
      .catch((error) => {
        if (requestId !== loadRequestRef.current) {
          return;
        }

        if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
          return;
        }

        if (!isSCRV2DetailComplete(initialReport)) {
          setReport(initialReport.scrNo || initialReport.distributorName ? initialReport : { scrId });
        }
      })
      .finally(() => {
        if (requestId === loadRequestRef.current) {
          setIsRefreshing(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrId]);

  useEffect(() => {
    if (
      !scrId ||
      !props.detail?.scrId ||
      !matchesScrV2Id(props.detail.scrId, scrId) ||
      !isSCRV2DetailComplete(props.detail)
    ) {
      return;
    }

    setReport(normalizeSCRV2DetailReport(props.detail, scrId));
    setIsRefreshing(false);
  }, [props.detail, scrId]);

  useEffect(() => {
    if (scrId && report?.scrId && String(report.scrId).toLowerCase() === String(scrId).toLowerCase()) {
      prefetchSCRV2SummaryReport(scrId);
    }
  }, [scrId, report?.scrId]);

  const highlights = useMemo(() => getReportHighlights(report), [report]);

  const invoiceRows = useMemo(() => buildNormalInvoiceRows(report), [report]);
  const activitySections = useMemo(() => buildInvoiceActivitySections(report), [report]);

  const filteredSections = useMemo(
    () =>
      activitySections.map((section) => ({
        ...section,
        filteredRows: filterInvoiceRows(section.rows, searchText),
      })),
    [activitySections, searchText]
  );

  const toggleSection = useCallback((sectionId) => {
    setOpenSections((current) => {
      const isOpen = current[sectionId] !== false;
      return {
        ...current,
        [sectionId]: !isOpen,
      };
    });
  }, []);

  const openInvoiceDetail = useCallback(
    async (row) => {
      if (!scrId || !row?.shopOrderId) {
        return;
      }

      const requestId = ++invoiceDetailRequestRef.current;
      const cachedDetail = peekSCRV2InvoiceDetailCache(scrId, row.shopOrderId);

      setSelectedInvoiceRow(row);
      setInvoiceDetail(cachedDetail);
      setInvoiceDetailError(null);
      setInvoiceDetailLoading(!cachedDetail);

      try {
        const detail = await fetchSCRV2InvoiceDetail(scrId, row.shopOrderId, {
          isFinalized: Boolean(report?.isFinalized),
        });
        if (requestId !== invoiceDetailRequestRef.current) {
          return;
        }
        setInvoiceDetail(detail);
      } catch (error) {
        if (requestId !== invoiceDetailRequestRef.current) {
          return;
        }

        if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
          return;
        }

        if (!cachedDetail) {
          setInvoiceDetailError(
            error?.response?.data?.message ||
            error?.response?.data?.Message ||
            error?.message ||
            "Failed to load SCR invoice detail."
          );
        }
      } finally {
        if (requestId === invoiceDetailRequestRef.current) {
          setInvoiceDetailLoading(false);
        }
      }
    },
    [scrId, report?.isFinalized]
  );

  const prefetchInvoiceDetail = useCallback(
    (row) => {
      if (!scrId || !row?.shopOrderId) {
        return;
      }

      prefetchSCRV2InvoiceDetail(scrId, row.shopOrderId, {
        isFinalized: Boolean(report?.isFinalized),
      });
    },
    [scrId, report?.isFinalized]
  );

  const closeInvoiceDetail = () => {
    invoiceDetailRequestRef.current += 1;
    setSelectedInvoiceRow(null);
    setInvoiceDetail(null);
    setInvoiceDetailError(null);
    setInvoiceDetailLoading(false);
  };

  const [isExporting, setIsExporting] = useState(false);

  const exportRows = async () => {
    if (!scrId || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      await exportSCRV2InvoiceWiseDetail(scrId);
    } finally {
      setIsExporting(false);
    }
  };

  const canPrint = Boolean(scrId) && hasFullReport && !showTablePlaceholders;

  const handleBeforePrint = () =>
    new Promise((resolve) => {
      printPayloadRef.current = {
        report,
        highlights,
        sections: filteredSections,
        searchText,
      };
      setIsPrintMountReady(true);
      window.setTimeout(resolve, 120);
    });

  const handleAfterPrint = () => {
    setIsPrintMountReady(false);
    printPayloadRef.current = null;
  };

  if (scrId && !report.scrNo && !report.distributorName && isRefreshing) {
    return (
      <VzPageContainer title="SCR Invoice Wise Detail">
        <InvoiceDetailPageShellSkeleton />
      </VzPageContainer>
    );
  }

  return (
    <VzPageContainer title="SCR Invoice Wise Detail">
      <div className="scr-v2-summary-screen scr-invoice-detail-screen">
        <div className="scr-summary-page-header d-flex align-items-center flex-wrap mb-2">
          <div className="scr-summary-page-header__title">
            <h5 className="mb-0 d-flex align-items-center flex-wrap">
              <b>SCR Invoice Wise Detail</b>
              {isRefreshing ? (
                <span className="badge badge-light border text-muted ml-2 mt-1 mt-sm-0 d-print-none font-weight-normal">
                  <i className="fas fa-sync-alt fa-spin mr-1" />
                  Updating
                </span>
              ) : null}
            </h5>
          </div>

          <div className="scr-summary-page-header__spacer flex-grow-1" aria-hidden="true" />

          <div className="scr-summary-page-header__status text-right">
            <Badge bg={report.isFinalized ? "success" : "warning"}>
              {report.isFinalized ? "Finalized" : "Open"}
            </Badge>
            <div className="text-muted small">
              SCR No: <span className="text-primary font-weight-bold">{report.scrNo || "-"}</span>
            </div>
          </div>
        </div>

        <div className="card border mb-2 scr-summary-info-card" style={NESTED_CARD_STYLE}>
          <div className="card-body py-2 px-2">
            <Row className="align-items-center mx-0">
              {renderSummaryColumn("Distributor", report.distributorName, { lg: 3 })}
              {renderSummaryColumn("Distribution Center", report.distCenterName, { lg: 3 })}
              {renderSummaryColumn(
                "DLM Details",
                <>
                  {report.dlmName || report.dlmUserName || "-"}
                  {report.deliveryQRCode ? (
                    <small className="scr-summary-info-card__subvalue d-block">
                      {report.deliveryQRCode}
                    </small>
                  ) : null}
                </>,
                { lg: 2 }
              )}
              {renderSummaryColumn(
                "Business Date",
                <>
                  {renderScrV2IdentityDate(report.businessDate)}
                  {report.finalizedDate ? (
                    <small className="scr-summary-info-card__subvalue d-block">
                      Finalized: {renderScrV2IdentityDate(report.finalizedDate)}
                    </small>
                  ) : null}
                </>,
                { lg: 2 }
              )}
              {renderSummaryColumn("Duration", formatScrV2DurationDays(report.durationDays), {
                lg: 2,
              })}
            </Row>
          </div>
        </div>

        <Row className="mb-2 mx-0 align-items-stretch scr-summary-kpi-row">
          <SummaryKpi
            colClassName="ps-0 pe-lg-1"
            icon="fa-file-invoice"
            iconBg="bg-primary"
            title="Total Payment Due"
            amount={formatCurrency(highlights.totalDue)}
            detail={`${highlights.totalInvoices} invoice(s) total`}
          />
          <SummaryKpi
            colClassName="px-lg-1"
            icon="fa-money-bill-wave"
            iconBg="bg-success"
            title="Total Payment Received"
            amount={formatCurrency(highlights.totalReceived)}
            detail={`${highlights.collectionRate}% collection rate`}
          />
          <SummaryKpi
            colClassName="ps-lg-1 pe-0"
            icon="fa-balance-scale"
            iconBg={
              highlights.variance > 0
                ? "bg-danger"
                : highlights.variance < 0
                  ? "bg-warning"
                  : "bg-info"
            }
            title={`${highlights.varianceLabel} (Variance)`}
            amount={formatCurrency(Math.abs(highlights.variance))}
            detail={
              highlights.variance === 0 ? "Fully balanced" : `${highlights.varianceLabel} amount`
            }
          />
        </Row>

        <div className="card border mb-2 scr-invoice-table-card" style={NESTED_CARD_STYLE}>
          <div className="card-header py-2 px-2 bg-white border-bottom">
            <Row className="align-items-center mx-0">
              <Col xs={12} lg={6} className="px-0 mb-2 mb-lg-0">
                <div className="d-flex align-items-center">
                  <span className="font-weight-bold text-dark" style={{ fontSize: "0.95rem" }}>Invoices</span>
                  <span className="badge badge-light border text-dark ml-2" style={{ fontSize: "0.8rem" }}>
                    {hasFullReport ? invoiceRows.length : isRefreshing ? "…" : "—"}
                  </span>
                </div>
              </Col>
              <Col xs={12} lg={6} className="px-0 d-print-none scr-invoice-toolbar">
                <div className="d-flex align-items-center justify-content-lg-end flex-nowrap flex-wrap scr-invoice-toolbar__row">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mr-2 flex-shrink-0"
                    onClick={exportRows}
                    disabled={!scrId || isExporting}
                  >
                    {isExporting ? (
                      <>
                        <Spinner animation="border" size="sm" className="mr-1" />
                        Exporting
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download mr-1" />
                        Export
                      </>
                    )}
                  </Button>
                  <Form.Control
                    size="sm"
                    type="search"
                    style={{ width: "220px", maxWidth: "100%" }}
                    placeholder="Search by shop or invoice..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="card-body p-0">
            {showTablePlaceholders ? <InvoiceActivitySectionsSkeleton /> : null}
            {!showTablePlaceholders && !hasFullReport ? (
              <div className="text-center text-muted py-4">
                Waiting for invoice details…
              </div>
            ) : null}
            {!showTablePlaceholders && hasFullReport && filteredSections.length === 0 ? (
              <div className="text-center text-muted py-4">
                No invoices found.
              </div>
            ) : null}
            {!showTablePlaceholders &&
              hasFullReport &&
              filteredSections.map((section) => {
                const isOpen = openSections[section.id] !== false;
                return (
                  <div
                    key={section.id}
                    className={`scr-invoice-activity-section scr-invoice-activity-section--${section.id}`}
                  >
                    <button
                      type="button"
                      className="scr-invoice-activity-section__header"
                      onClick={() => toggleSection(section.id)}
                      aria-expanded={isOpen}
                    >
                      <span className="scr-invoice-activity-section__title">
                        <span className="scr-invoice-activity-section__icon" aria-hidden="true">
                          <i className={section.iconClass} />
                        </span>
                        <span className="scr-invoice-activity-section__label">{section.title}</span>
                        <span className="scr-invoice-activity-section__count">
                          {section.filteredRows.length}
                        </span>
                        {section.totalAmount > 0 ? (
                          <span className="scr-invoice-activity-section__amount">
                            {formatCurrency(section.totalAmount)}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`scr-invoice-activity-section__chevron${isOpen ? " is-expanded" : ""}`}
                        aria-hidden="true"
                      >
                        <i className="fas fa-chevron-down" />
                      </span>
                    </button>
                    <Collapse in={isOpen}>
                      <div className="scr-invoice-activity-section__body">
                        <InvoiceActivityTable
                          rows={section.filteredRows}
                          openInvoiceDetail={openInvoiceDetail}
                          prefetchInvoiceDetail={prefetchInvoiceDetail}
                        />
                      </div>
                    </Collapse>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {isPrintMountReady && printPayloadRef.current ? (
        <div style={{ display: "none" }} aria-hidden="true">
          <SCRV2CompleteDetailPrint
            ref={printRef}
            report={printPayloadRef.current.report}
            highlights={printPayloadRef.current.highlights}
            sections={printPayloadRef.current.sections}
            searchText={printPayloadRef.current.searchText}
          />
        </div>
      ) : null}

      <section className="d-print-none mt-2 pt-2 border-top scr-summary-actions scr-invoice-detail-actions">
        <Row className="mx-0">
          <Col className="px-0 d-flex justify-content-between align-items-center flex-wrap">
            <ReactToPrint
              trigger={() => (
                <button
                  style={{ minInlineSize: 100 }}
                  className="btn btn-default"
                  type="button"
                  disabled={!canPrint}
                >
                  <i className="fa fa-print mr-1" />
                  Print
                </button>
              )}
              content={() => printRef.current}
              onBeforeGetContent={handleBeforePrint}
              onAfterPrint={handleAfterPrint}
              pageStyle={`
                @page {
                  size: A4 portrait;
                  margin: 10mm;
                }
                @media print {
                  html, body {
                    width: 100% !important;
                    height: auto !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .scr-v2-complete-detail-print-view {
                    width: 100% !important;
                    max-width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                }
              `}
            />
            <Button
              variant="primary"
              size="sm"
              className="font-weight-bold mt-2 mt-sm-0"
              onMouseEnter={() => prefetchSCRV2SummaryReport(scrId)}
              onFocus={() => prefetchSCRV2SummaryReport(scrId)}
              onClick={() =>
                history.push({
                  pathname: `/SCRV2SummaryReport/${scrId}`,
                  state: {
                    preview: buildSCRV2SummaryPreview({ ...report, scrId }),
                  },
                })
              }
            >
              Back to Summary
              <i className="fa fa-arrow-left ml-1" />
            </Button>
          </Col>
        </Row>
      </section>

      <SCRV2InvoiceDetailModal
        show={!!selectedInvoiceRow}
        rowContext={selectedInvoiceRow}
        detail={invoiceDetail}
        loading={invoiceDetailLoading}
        error={invoiceDetailError}
        report={report}
        onClose={closeInvoiceDetail}
        onPreviewFile={setFilePreview}
      />
      <FilePreviewModal preview={filePreview} onClose={() => setFilePreview(null)} />
    </VzPageContainer>
  );
};

const mapStateToProps = (state) => ({
  detail: state.SCRV2DetailReport?.detail,
});

export default connect(mapStateToProps, {
  getSCRV2CompleteDetail,
})(SCRV2CompleteDetailReport);
